/**
 * Keyboard and open/close behaviour for the app's custom dropdowns.
 *
 * ## Why this exists
 *
 * CoverageSelector, FormationSelector and PositionAssignmentSelector are all the same widget: a
 * `<div role="combobox">` trigger over a `<ul role="listbox">` of `<li role="option">`. All three
 * had good ARIA attributes and all three were **unusable by keyboard once opened**: the only key
 * handler was on the trigger (Enter/Space to toggle, Escape to close), the options were plain `<li>`
 * elements with no `tabIndex` and no key handling, and nothing tracked an active option. So a
 * keyboard user could open the menu and then had no way to move through it or choose anything.
 *
 * Rather than fix that three times, the behaviour lives here.
 *
 * ## Why not just use a native <select>
 *
 * For a plain one-of-N list, we should — and RouteControls does exactly that. These three carry
 * per-option badges, lock states, grouped sections and descriptions, which a native `<option>`
 * cannot render. That is the only reason they are custom.
 *
 * ## The pattern
 *
 * Follows the ARIA combobox-with-listbox pattern: focus stays on the trigger at all times and the
 * active option is communicated with `aria-activedescendant`. That avoids moving DOM focus into a
 * list of non-focusable elements.
 *
 * Disabled options are deliberately kept **navigable**. Skipping them would hide them from keyboard
 * users, and in this app the disabled rows are the premium ones — the whole point is that somebody
 * finds them and learns they exist. Whether activating one does anything is the caller's decision:
 * the hook just reports the index.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface UseListboxParams {
  /** Number of options in the list. */
  itemCount: number;
  /** Index of the selected option, or -1 when nothing is selected. */
  selectedIndex: number;
  /** Fired when the user chooses the option at `index` — by click, Enter or Space. */
  onActivate: (index: number) => void;
  /** Base id used to derive the listbox and option ids. Must be unique on the page. */
  id: string;
  /** When true the trigger is inert and cannot be opened. */
  disabled?: boolean;
}

export interface UseListboxResult {
  isOpen: boolean;
  /** The option the keyboard is currently on, or -1. Not the same as the selected option. */
  activeIndex: number;
  close: () => void;
  /** Wrap the whole widget in this so outside clicks can be detected. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Spread onto the `role="combobox"` trigger. */
  triggerProps: {
    role: 'combobox';
    tabIndex: number;
    'aria-expanded': boolean;
    'aria-haspopup': 'listbox';
    'aria-controls': string;
    'aria-activedescendant': string | undefined;
    onClick: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  };
  /** Spread onto the `role="listbox"` element. */
  listProps: { role: 'listbox'; id: string };
  /**
   * Spread onto each option element.
   *
   * Deliberately does NOT include `role="option"`. The caller sets that in JSX so that static a11y
   * linting can see the role and accept `aria-selected` alongside it — a role arriving via a spread
   * is invisible to that analysis.
   */
  getOptionProps: (index: number) => {
    id: string;
    onClick: () => void;
    onMouseEnter: () => void;
    'data-active': boolean;
  };
}

export function useListbox({
  itemCount,
  selectedIndex,
  onActivate,
  id,
  disabled = false,
}: UseListboxParams): UseListboxResult {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const listboxId = `${id}-listbox`;
  const optionId = useCallback((index: number) => `${id}-option-${index}`, [id]);

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  /** Opening lands on the current selection, so arrow keys continue from where the user is. */
  const open = useCallback(() => {
    setIsOpen(true);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [selectedIndex]);

  // Close on a click anywhere outside the widget.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, close]);

  // The list is scrollable (max-height), so keyboard navigation has to bring the active option
  // into view or it walks off the bottom invisibly.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const element = document.getElementById(optionId(activeIndex));
    // Guarded because jsdom does not implement scrollIntoView.
    element?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, activeIndex, optionId]);

  // A list that shrinks (e.g. fewer coverages after a formation change) must not leave the active
  // index pointing past the end.
  useEffect(() => {
    setActiveIndex((current) => (current >= itemCount ? itemCount - 1 : current));
  }, [itemCount]);

  const move = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        const from = current < 0 ? (selectedIndex >= 0 ? selectedIndex : 0) : current;
        // Clamped rather than wrapping, matching the ARIA listbox pattern.
        return Math.min(itemCount - 1, Math.max(0, from + delta));
      });
    },
    [itemCount, selectedIndex]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isOpen) {
            open();
          } else if (activeIndex >= 0) {
            onActivate(activeIndex);
          }
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) open();
          else move(1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) open();
          else move(-1);
          break;

        case 'Home':
          if (isOpen) {
            event.preventDefault();
            setActiveIndex(0);
          }
          break;

        case 'End':
          if (isOpen) {
            event.preventDefault();
            setActiveIndex(itemCount - 1);
          }
          break;

        case 'Escape':
          // Focus is already on the trigger under this pattern, so there is nothing to restore.
          close();
          break;

        case 'Tab':
          // Let focus leave normally, but do not leave an orphaned menu open behind it.
          close();
          break;

        default:
          break;
      }
    },
    [disabled, isOpen, activeIndex, itemCount, move, onActivate, open, close]
  );

  const triggerProps = useMemo(
    () => ({
      role: 'combobox' as const,
      tabIndex: disabled ? -1 : 0,
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox' as const,
      'aria-controls': listboxId,
      // This is what tells a screen reader which option the keyboard is on, without moving focus
      // off the trigger. Its absence was why the previous implementation announced nothing.
      'aria-activedescendant': isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined,
      onClick: () => {
        if (disabled) return;
        if (isOpen) close();
        else open();
      },
      onKeyDown,
    }),
    [disabled, isOpen, activeIndex, listboxId, optionId, onKeyDown, open, close]
  );

  const getOptionProps = useCallback(
    (index: number) => ({
      id: optionId(index),
      onClick: () => onActivate(index),
      // Hovering moves the active option so the pointer and keyboard agree on one highlight.
      onMouseEnter: () => setActiveIndex(index),
      'data-active': index === activeIndex,
    }),
    [optionId, onActivate, activeIndex]
  );

  return {
    isOpen,
    activeIndex,
    close,
    containerRef,
    triggerProps,
    listProps: { role: 'listbox', id: listboxId },
    getOptionProps,
  };
}
